import React from 'react';
import { Award, Shield, Download, CheckCircle2 } from 'lucide-react';

const COMPLETION_CERT = 'https://customer-assets.emergentagent.com/job_8d33ea52-c764-4e32-b8e2-de0e8040a6fa/artifacts/mw7t8gj7_Course%20Completion%20Certificate.png';
const WORK_CERT = 'https://customer-assets.emergentagent.com/job_8d33ea52-c764-4e32-b8e2-de0e8040a6fa/artifacts/7f7qamk1_Industrial%20Job%20Certificate.png';

/**
 * Premium "Dual Certificates You'll Earn" section.
 * Themes: orange (film), emerald (game), yellow (jewellery), violet (blender)
 */
const CertificatesSection = ({ theme = 'orange', courseName = 'Mastering Film Making & Editing' }) => {
  const themes = {
    orange: { ring: 'ring-orange-500/40', glow: 'from-orange-500 via-amber-400 to-yellow-500', text: 'text-orange-300', border: 'border-orange-500/30', bg: 'from-black via-orange-950/40 to-amber-950/30', orbA: 'bg-orange-500/15', orbB: 'bg-amber-500/15', badge: 'bg-orange-500/20 border-orange-500/40 text-orange-300', accent: 'text-orange-400', shadow: 'shadow-orange-500/30' },
    emerald: { ring: 'ring-emerald-500/40', glow: 'from-emerald-500 via-cyan-400 to-teal-500', text: 'text-emerald-300', border: 'border-emerald-500/30', bg: 'from-black via-emerald-950/40 to-cyan-950/30', orbA: 'bg-emerald-500/15', orbB: 'bg-cyan-500/15', badge: 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300', accent: 'text-emerald-400', shadow: 'shadow-emerald-500/30' },
    yellow: { ring: 'ring-yellow-500/40', glow: 'from-yellow-500 via-amber-400 to-orange-500', text: 'text-yellow-300', border: 'border-yellow-500/30', bg: 'from-black via-yellow-950/40 to-amber-950/30', orbA: 'bg-yellow-500/15', orbB: 'bg-amber-500/15', badge: 'bg-yellow-500/20 border-yellow-500/40 text-yellow-300', accent: 'text-yellow-400', shadow: 'shadow-yellow-500/30' },
    violet: { ring: 'ring-violet-500/40', glow: 'from-violet-500 via-purple-400 to-pink-500', text: 'text-violet-300', border: 'border-violet-500/30', bg: 'from-black via-violet-950/40 to-purple-950/30', orbA: 'bg-violet-500/15', orbB: 'bg-pink-500/15', badge: 'bg-violet-500/20 border-violet-500/40 text-violet-300', accent: 'text-violet-400', shadow: 'shadow-violet-500/30' }
  };
  const t = themes[theme] || themes.orange;

  const certs = [
    {
      img: COMPLETION_CERT,
      tag: 'Industry Recognized',
      title: 'Course Completion Certificate',
      sub: 'Co-branded by AI School For All × Workshop Library',
      bullets: ['Verifiable Certificate ID', 'Lifetime credential', 'Recognized by 500+ creative studios', 'Shareable on LinkedIn']
    },
    {
      img: WORK_CERT,
      tag: 'Real Work Experience',
      title: 'Industry Work Experience Certificate',
      sub: 'Issued by Infinite Web Marketing Pvt. Ltd. (15+ Years in Industry)',
      bullets: ['3-Month industry experience', 'Real client project exposure', 'Authorised company stamp', 'Boosts resume & freelance credibility']
    }
  ];

  return (
    <section data-testid="certificates-section" className={`py-24 px-6 bg-gradient-to-br ${t.bg} relative overflow-hidden`}>
      <div className={`absolute top-1/4 left-0 w-[500px] h-[500px] rounded-full ${t.orbA} blur-[140px]`}></div>
      <div className={`absolute bottom-1/4 right-0 w-[400px] h-[400px] rounded-full ${t.orbB} blur-[120px]`}></div>
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.3) 0 1px, transparent 1px 60px)' }}></div>

      <div className="container mx-auto max-w-7xl relative">
        <div className="text-center mb-14">
          <div className={`inline-flex items-center gap-2 ${t.badge} border rounded-full px-5 py-2 mb-5`}>
            <Award className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-widest">Dual Certifications</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4 leading-tight text-white">
            You'll Walk Away With <span className={`text-transparent bg-clip-text bg-gradient-to-r ${t.glow}`}>2 Premium Certificates</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Not just a learning certificate — a real industry-recognised <span className="font-bold text-white">Work Experience Certificate</span> that boosts your career instantly.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {certs.map((c, i) => (
            <div key={i} className="group relative" data-testid={`certificate-card-${i}`}>
              <div className={`absolute -inset-1 bg-gradient-to-r ${t.glow} rounded-3xl blur-lg opacity-30 group-hover:opacity-60 transition-opacity`}></div>
              <div className={`relative bg-black/60 backdrop-blur-sm rounded-3xl border ${t.border} overflow-hidden ${t.shadow} shadow-2xl`}>
                <div className="relative bg-white/95 p-4">
                  <img
                    src={c.img}
                    alt={c.title}
                    className="w-full h-auto rounded-xl shadow-xl group-hover:scale-[1.02] transition-transform duration-500"
                    data-testid={`certificate-img-${i}`} decoding="async" fetchPriority="high" loading="eager" />
                  <div className={`absolute top-7 left-7 ${t.badge} border backdrop-blur-md rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest`}>
                    {c.tag}
                  </div>
                </div>
                <div className="p-7">
                  <h3 className="text-2xl font-black text-white mb-1">{c.title}</h3>
                  <p className={`text-sm font-semibold ${t.text} mb-4`}>{c.sub}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {c.bullets.map((b, j) => (
                      <div key={j} className="flex items-start gap-2 text-xs text-gray-300">
                        <CheckCircle2 className={`w-4 h-4 ${t.accent} flex-shrink-0 mt-0.5`} />
                        <span>{b}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-center gap-4">
          <div className={`inline-flex items-center gap-3 ${t.badge} border rounded-2xl px-6 py-3`}>
            <Shield className="w-5 h-5" />
            <span className="text-sm font-bold">Both certificates are sample previews. Earn yours after course completion + 3-month industry attachment.</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CertificatesSection;
